/**
 * 支付相关场景适配（paywall）
 *
 * 触发：支付失败 / 取消回跳（及后续其它支付结果点），非冷启、非付费墙 mount/unmount。
 * 流程：ResolveOffersV2 → transform → upsert session / enqueue →
 *       若当前 activeScene 可展示则 tryShow；否则（如 reader → null）只落 store，
 *       等跳页后由 SceneSync.onScene 再展示。
 * 禁止抢写 activeScene（回跳后真相源归路由 / SceneSync）。
 */

import { ActivityApi } from '@/apis/activity';
import { useActivityQueueStore } from '@/store/activity-queue';
import { useActivitySessionStore } from '@/store/activity-session';

import { resolveActivityDefaults } from '../defaults';
import { transformOfferResolve } from '../transform-offer';

import type { ITransformOfferItemResult } from '../transform-offer';

/** 支付挽留 ResolveOffers 结果：用于「有弹窗则不出取消/失败 toast」 */
export interface IPaywallOfferResult {
    /** 是否写入过 task / session */
    wrote: boolean;
    /** 是否当场 tryShow 成功（弹窗已进 current） */
    shown: boolean;
    /**
     * 应跳过取消/失败 toast。
     * true：当场弹出挽留、队列已有弹窗占位、或同单已处理。
     * false：无可见弹窗，可 toast（含只落队列 / 名额用尽 / resolve 空）。
     */
    suppressToast: boolean;
}

/** 同单防重：按已处理压 toast，避免弹窗已开时再出取消提示 */
const DUP_SUPPRESS_TOAST: IPaywallOfferResult = { wrote: false, shown: true, suppressToast: true };

/** 同一订单只触发一次挽留 resolve，避免 cancel 多路径重复入队 */
const retentionResolvedOrderIds = new Set<string>();

/** 登录态切换时清空支付挽留订单防重记录。 */
export function resetPaywallOfferResolvedOrders(): void {
    retentionResolvedOrderIds.clear();
}

/**
 * ResolveOffers 结果统一落库：session upsert + task 入队。
 * @returns wrote 是否写入过 task / session；
 *          hostBoundActivityIds 本次入队任务中绑定承接页（requiresPaywallHost）的活动 id，
 *          供「到墙」入口判断任务是否需要随承接页在场状态丢弃
 */
function applyOfferItems(items: ITransformOfferItemResult[]): { wrote: boolean; hostBoundActivityIds: Set<string> } {
    let wrote = false;
    const hostBoundActivityIds = new Set<string>();
    items.forEach(item => {
        if (item.session) {
            useActivitySessionStore.getState().upsertSession(item.session);
            wrote = true;
        }
        if (item.task) {
            useActivityQueueStore.getState().enqueue(item.task);
            wrote = true;
            if (resolveActivityDefaults(item.task.type).requiresPaywallHost) {
                hostBoundActivityIds.add(item.task.activityId);
            }
        }
    });
    return { wrote, hostBoundActivityIds };
}

/**
 * 组装结果：有 current 或本次 shown 时压 toast，避免与活动弹窗叠放。
 * @param wrote 是否写入过 task/session
 * @param shown 是否当场 tryShow 成功
 */
function buildOfferResult(wrote: boolean, shown: boolean): IPaywallOfferResult {
    const hasPopup = shown || Boolean(useActivityQueueStore.getState().current);
    return { wrote, shown, suppressToast: hasPopup };
}

/**
 * 支付相关 ResolveOffers：入队 / 写 session；有可信 activeScene 时才 tryShow。
 * 当前仅支付失败/取消（exit）场景调用；到墙打开拉取见 runPaywallOfferOnOpen。
 * @param unpaidOrderId 本次未支付的 commerce 订单 ID；非空时透传给活动接口
 * @returns wrote / shown / suppressToast；调用方用 suppressToast 决定是否打取消/失败 toast
 */
export async function runPaywallOffer(unpaidOrderId?: string): Promise<IPaywallOfferResult> {
    try {
        const res = await ActivityApi.resolveOffersV2({
            scene: 'paywall',
            event: '',
            action: 'activate',
            ...(unpaidOrderId ? { unpaid_order_id: unpaidOrderId } : {}),
        });
        const { items } = transformOfferResolve(res?.data, { scene: 'paywall' });

        const { wrote } = applyOfferItems(items);

        if (!wrote) {
            return buildOfferResult(false, false);
        }

        // 返回后再读 scene，避免回跳改 URL 与 resolve 竞态
        const { activeScene, tryShow } = useActivityQueueStore.getState();
        if (!activeScene) {
            // reader 等非可展示页：只落 store，跳页后由 onScene 续展；无 current 时允许 toast
            return buildOfferResult(true, false);
        }

        // 只 tryShow，不 resetEntry / force。
        // 【口径】整会话只自动弹 1 次（含挽留）：冷启已弹过则 shown=false，任务留在 waiting，属预期。
        const shown = tryShow(activeScene);
        return buildOfferResult(true, shown);
    } catch (error) {
        console.error('[runPaywallOffer] resolveOffersV2 failed', error);
        return buildOfferResult(false, false);
    }
}

/**
 * 支付失败 / 取消后拉取支付 offer（event=exit）。
 * @param orderId 可选；传入时按订单防重
 * @returns wrote / shown / suppressToast；suppressToast=true 时调用方应跳过取消/失败 toast
 */
export function runPaywallOfferAfterPaymentAbandon(orderId?: string): Promise<IPaywallOfferResult> {
    const id = orderId?.trim();
    if (id) {
        if (retentionResolvedOrderIds.has(id)) {
            return Promise.resolve(DUP_SUPPRESS_TOAST);
        }
        retentionResolvedOrderIds.add(id);
    }
    return runPaywallOffer(id);
}

/** 到墙拉取超时兜底：超时强制视为已落地，保证付费墙不被接口 hang 死 */
const OPEN_RESOLVE_SETTLE_TIMEOUT_MS = 3000;

/**
 * 当前挂载的付费墙承接页数量（reader 付费墙与 /pay 页 mount/unmount 维护）。
 * 到墙拉取（action=query）只发生在这些页面，但请求返回时页面可能已卸载
 * （滚动裁切 / 路由离开），此时绑定承接页的活动任务（配置见 defaults
 * requiresPaywallHost，当前仅假优惠）若残留，会被 SceneSync 在同 scene
 * 但无承接上下文的页面误弹。用该计数判断「到墙上下文」是否还在：
 * 请求返回时计数为 0 直接丢弃；请求已入队后墙全部卸载时，由
 * trackPaywallMounted 在计数归零时统一丢弃。
 */
let mountedPaywallCount = 0;

/** 丢弃当前队列中指定活动 id 的任务（含 current，若有）。 */
function purgeTasksByActivityIds(activityIds: Set<string>): void {
    activityIds.forEach(activityId => {
        useActivityQueueStore.getState().purgeActivityTasks(activityId);
    });
}

/** 丢弃 waiting 中所有绑定承接页（requiresPaywallHost）的任务。 */
function purgeHostBoundWaitingTasks(): void {
    const hostBoundActivityIds = new Set<string>();
    useActivityQueueStore.getState().waiting.forEach(task => {
        if (resolveActivityDefaults(task.type).requiresPaywallHost) {
            hostBoundActivityIds.add(task.activityId);
        }
    });
    purgeTasksByActivityIds(hostBoundActivityIds);
}

/** 付费墙承接页（reader 付费墙 / /pay）挂载 / 卸载时登记在场状态。 */
export function trackPaywallMounted(mounted: boolean): void {
    mountedPaywallCount = Math.max(0, mountedPaywallCount + (mounted ? 1 : -1));
    if (mounted || mountedPaywallCount > 0) {
        return;
    }
    // 最后一面承接墙卸载：丢弃残留的绑定承接页任务，否则下次进墙会被
    // PaywallActivityBootstrap 的 onScene 绕过视口门槛当场误弹。
    // 延迟到当前提交结束后再确认：换墙 / StrictMode 重挂载会让计数在同一提交内瞬间归零。
    setTimeout(() => {
        if (mountedPaywallCount === 0) {
            purgeHostBoundWaitingTasks();
        }
    }, 0);
}

/** 「到墙」入口场景：reader 付费墙用 paywall（key paywall:query:），/pay 充值中心用 profile（key profile:query:） */
export type TWallOpenScene = 'paywall' | 'profile';

/**
 * 打开付费墙 / 充值中心时拉取活动（action=query，event 为空）。
 * 假优惠不挂 paywall:activate:（该 key 与挽留 scene_unique_display 互抢），开墙一律走 query；
 * 是否下发假优惠弹窗等由服务端统一决策，前端只负责入队与当场 tryShow；
 * 与支付失败/取消的 exit 拉取互不干扰（entryCount 名额与互斥组仍由队列约束）。
 * @param scene reader 付费墙传 'paywall'，/pay 充值中心传 'profile'
 * @param opts.showOnArrival 响应到达时是否当场 tryShow。reader 付费墙传 false：
 *        拉取已提前到墙 mount，当场弹会盖住还在读预览的用户，展示统一由 70% 进视口驱动
 */
export function runPaywallOfferOnOpen(scene: TWallOpenScene, opts?: { showOnArrival?: boolean }): Promise<void> {
    const showOnArrival = opts?.showOnArrival ?? true;
    // 无在途防重：每个调用方（reader 墙 / /pay）各自发请求，并发重复由 enqueue 的 mutexGroup 去重兜底，
    // 避免跨 scene 复用同一 promise 导致 /pay 拿到 paywall 场景的服务端决策
    useActivityQueueStore.getState().setPaywallOfferStatus('pending');
    // 超时兜底：无论接口 hang 住还是最终报错，付费墙都必须能展示
    const settleTimer = setTimeout(() => {
        useActivityQueueStore.getState().setPaywallOfferStatus('settled');
    }, OPEN_RESOLVE_SETTLE_TIMEOUT_MS);

    return (async () => {
        try {
            const res = await ActivityApi.resolveOffersV2({
                scene,
                event: '',
                action: 'query',
            });
            // 假优惠等「到墙打开」类活动只允许在 query 入口入队（见 transform-offer 过滤）
            const { items } = transformOfferResolve(res?.data, { scene, action: 'query' });

            // 本次拉到的绑定承接页的任务 id：若请求返回时触发墙已不在场，直接丢弃而非残留队列
            const { hostBoundActivityIds } = applyOfferItems(items);

            const { activeScene, tryShow } = useActivityQueueStore.getState();
            // 绑定承接页的任务（当前仅假优惠）只在「到墙」上下文（reader 付费墙 / /pay）展示：
            // 若请求返回时已无承接页在场（滚动裁切 / 路由离开），直接丢弃，避免残留队列后被
            // SceneSync 误弹、白占会话 entryCount / claimed 导致真正的付费墙体验丢失。
            // 只看墙是否在场、不要求 activeScene：墙在场但场景尚未同步时任务留队列，
            // 等 SceneSync/onScene 续展，不误丢。
            if (hostBoundActivityIds.size > 0 && mountedPaywallCount === 0) {
                purgeTasksByActivityIds(hostBoundActivityIds);
            }
            // 其余任务保持原逻辑：有可信 activeScene 且允许当场展示时立即 tryShow；
            // reader 付费墙（showOnArrival=false）留在队列，由墙体 70% 进视口时统一展示；
            // 否则留在队列，等 SceneSync/onScene 续展
            if (activeScene && showOnArrival) {
                tryShow(activeScene);
            }
        } catch (error) {
            console.error('[runPaywallOfferOnOpen] resolveOffersV2 failed', error);
        } finally {
            clearTimeout(settleTimer);
            useActivityQueueStore.getState().setPaywallOfferStatus('settled');
        }
    })();
}
